"use client";

import logger from "@/lib/logger";
import {
	Maximize,
	Pause,
	Play,
	SkipBack,
	SkipForward,
	Volume2,
	VolumeX,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";

interface VideoPlayerProps {
	videoUrl: string;
	fileName: string;
	className?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
	videoUrl,
	fileName,
	className = "",
}) => {
	const t = useTranslations("videoPlayer");
	const videoRef = useRef<HTMLVideoElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [volume, setVolume] = useState(1);
	const [isMuted, setIsMuted] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [showControls, setShowControls] = useState(true);
	const [playbackRate, setPlaybackRate] = useState(1);

	// Auto-hide controls timer
	const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Format time display
	const formatTime = (time: number): string => {
		const hours = Math.floor(time / 3600);
		const minutes = Math.floor((time % 3600) / 60);
		const seconds = Math.floor(time % 60);

		if (hours > 0) {
			return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
		}
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	// Handle play/pause
	const togglePlayPause = useCallback(async () => {
		if (!videoRef.current) return;

		try {
			if (isPlaying) {
				videoRef.current.pause();
				setIsPlaying(false);
			} else {
				await videoRef.current.play();
				setIsPlaying(true);
			}
		} catch (error) {
			logger.error({ err: error }, "Error playing video");
			setIsPlaying(false);
		}
	}, [isPlaying]);

	// Handle seeking
	const handleSeek = useCallback(
		(values: number[]) => {
			if (!videoRef.current) return;
			const newTime = (values[0] / 100) * duration;
			videoRef.current.currentTime = newTime;
			setCurrentTime(newTime);
		},
		[duration],
	);

	// Handle volume change
	const handleVolumeChange = useCallback((values: number[]) => {
		const newVolume = values[0] / 100;
		setVolume(newVolume);
		setIsMuted(newVolume === 0);
		if (videoRef.current) {
			videoRef.current.volume = newVolume;
			videoRef.current.muted = newVolume === 0;
		}
	}, []);

	// Toggle mute
	const toggleMute = useCallback(() => {
		if (!videoRef.current) return;

		const newMuted = !isMuted;
		setIsMuted(newMuted);
		videoRef.current.muted = newMuted;

		if (newMuted) {
			videoRef.current.volume = 0;
		} else {
			videoRef.current.volume = volume;
		}
	}, [isMuted, volume]);

	// Skip forward/backward
	const skipForward = useCallback(() => {
		if (!videoRef.current) return;
		videoRef.current.currentTime = Math.min(
			videoRef.current.currentTime + 10,
			duration,
		);
	}, [duration]);

	const skipBackward = useCallback(() => {
		if (!videoRef.current) return;
		videoRef.current.currentTime = Math.max(
			videoRef.current.currentTime - 10,
			0,
		);
	}, []);

	// Handle playback rate change
	const handlePlaybackRateChange = useCallback((rate: number) => {
		if (!videoRef.current) return;
		videoRef.current.playbackRate = rate;
		setPlaybackRate(rate);
	}, []);

	// Toggle fullscreen
	const toggleFullscreen = useCallback(async () => {
		if (!containerRef.current) return;

		try {
			if (!isFullscreen) {
				if (containerRef.current.requestFullscreen) {
					await containerRef.current.requestFullscreen();
				} else if ((containerRef.current as any).webkitRequestFullscreen) {
					await (containerRef.current as any).webkitRequestFullscreen();
				}
			} else {
				if (document.exitFullscreen) {
					await document.exitFullscreen();
				} else if ((document as any).webkitExitFullscreen) {
					await (document as any).webkitExitFullscreen();
				}
			}
		} catch (error) {
			logger.error({ err: error }, "Error toggling fullscreen");
		}
	}, [isFullscreen]);

	// Auto-hide controls
	const resetControlsTimeout = useCallback(() => {
		setShowControls(true);
		if (controlsTimeoutRef.current) {
			clearTimeout(controlsTimeoutRef.current);
		}
		if (isPlaying) {
			controlsTimeoutRef.current = setTimeout(() => {
				setShowControls(false);
			}, 3000);
		}
	}, [isPlaying]);

	// Handle mouse movement
	const handleMouseMove = useCallback(() => {
		resetControlsTimeout();
	}, [resetControlsTimeout]);

	// Keyboard shortcuts
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!videoRef.current) return;

			switch (event.code) {
				case "Space":
					event.preventDefault();
					togglePlayPause();
					break;
				case "ArrowLeft":
					event.preventDefault();
					skipBackward();
					break;
				case "ArrowRight":
					event.preventDefault();
					skipForward();
					break;
				case "KeyM":
					event.preventDefault();
					toggleMute();
					break;
				case "KeyF":
					event.preventDefault();
					toggleFullscreen();
					break;
			}
		},
		[togglePlayPause, skipBackward, skipForward, toggleMute, toggleFullscreen],
	);

	// Setup event listeners
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		const handleLoadedMetadata = () => {
			setDuration(video.duration);
			setIsLoading(false);
		};

		const handleTimeUpdate = () => {
			setCurrentTime(video.currentTime);
		};

		const handleEnded = () => {
			setIsPlaying(false);
			setCurrentTime(0);
			video.currentTime = 0;
		};

		const handleCanPlay = () => {
			setIsLoading(false);
		};

		const handleVolumeChange = () => {
			setVolume(video.volume);
			setIsMuted(video.muted);
		};

		video.addEventListener("loadedmetadata", handleLoadedMetadata);
		video.addEventListener("timeupdate", handleTimeUpdate);
		video.addEventListener("ended", handleEnded);
		video.addEventListener("canplay", handleCanPlay);
		video.addEventListener("volumechange", handleVolumeChange);

		return () => {
			video.removeEventListener("loadedmetadata", handleLoadedMetadata);
			video.removeEventListener("timeupdate", handleTimeUpdate);
			video.removeEventListener("ended", handleEnded);
			video.removeEventListener("canplay", handleCanPlay);
			video.removeEventListener("volumechange", handleVolumeChange);
		};
	}, [videoUrl]);

	// Fullscreen change event
	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);
		document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
			document.removeEventListener(
				"webkitfullscreenchange",
				handleFullscreenChange,
			);
		};
	}, []);

	// Keyboard event listener
	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [handleKeyDown]);

	// Controls auto-hide
	useEffect(() => {
		resetControlsTimeout();
		return () => {
			if (controlsTimeoutRef.current) {
				clearTimeout(controlsTimeoutRef.current);
			}
		};
	}, [isPlaying, resetControlsTimeout]);

	// Click to play/pause
	const handleVideoClick = useCallback(() => {
		togglePlayPause();
		resetControlsTimeout();
	}, [togglePlayPause, resetControlsTimeout]);

	return (
		<Card className={`overflow-hidden ${className}`}>
			<CardContent className="p-0">
				{/* File info */}
				<div className="p-4 border-b">
					<h3 className="text-lg font-bold text-primary">{fileName}</h3>
					<p className="text-sm text-muted-foreground">
						{isLoading ? t("loading") : `${formatTime(duration)}`}
					</p>
				</div>

				{/* Video container */}
				<div
					ref={containerRef}
					className={`relative bg-black ${isFullscreen ? "h-screen" : "aspect-video"}`}
					onMouseMove={handleMouseMove}
					onMouseLeave={() => isPlaying && setShowControls(false)}
				>
					{/* Video element */}
					<video
						ref={videoRef}
						src={videoUrl}
						className="w-full h-full object-contain cursor-pointer"
						onClick={handleVideoClick}
						preload="metadata"
						playsInline
						aria-label={fileName}
					/>

					{/* Loading overlay */}
					{isLoading && (
						<div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
							<div className="text-white text-center">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
								<p>{t("loadingVideo")}</p>
							</div>
						</div>
					)}

					{/* Controls overlay */}
					{showControls && !isLoading && (
						<div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent">
							<div className="w-full p-4 space-y-4">
								{/* Progress bar */}
								<Slider
									value={[duration > 0 ? (currentTime / duration) * 100 : 0]}
									onValueChange={handleSeek}
									max={100}
									step={0.1}
									className="w-full"
									disabled={isLoading}
									aria-label={t("seek")}
								/>

								{/* Control buttons and info */}
								<div className="flex items-center justify-between text-white">
									{/* Left controls */}
									<div className="flex items-center space-x-3">
										<Button
											variant="ghost"
											size="sm"
											onClick={skipBackward}
											disabled={isLoading}
											className="text-white hover:text-white hover:bg-white/20"
											aria-label={t("skipBack")}
										>
											<SkipBack className="w-4 h-4" aria-hidden="true" />
										</Button>

										<Button
											variant="ghost"
											size="lg"
											onClick={togglePlayPause}
											disabled={isLoading}
											className="text-white hover:text-white hover:bg-white/20"
											aria-label={isPlaying ? t("pause") : t("play")}
										>
											{isPlaying ? (
												<Pause className="w-6 h-6" aria-hidden="true" />
											) : (
												<Play className="w-6 h-6" aria-hidden="true" />
											)}
										</Button>

										<Button
											variant="ghost"
											size="sm"
											onClick={skipForward}
											disabled={isLoading}
											className="text-white hover:text-white hover:bg-white/20"
											aria-label={t("skipForward")}
										>
											<SkipForward className="w-4 h-4" aria-hidden="true" />
										</Button>

										{/* Volume controls */}
										<div className="flex items-center space-x-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={toggleMute}
												className="text-white hover:text-white hover:bg-white/20"
												aria-label={isMuted || volume === 0 ? t("unmute") : t("mute")}
											>
												{isMuted || volume === 0 ? (
													<VolumeX className="w-4 h-4" aria-hidden="true" />
												) : (
													<Volume2 className="w-4 h-4" aria-hidden="true" />
												)}
											</Button>
											<Slider
												value={[isMuted ? 0 : volume * 100]}
												onValueChange={handleVolumeChange}
												max={100}
												step={1}
												className="w-20"
												aria-label={t("volume")}
											/>
										</div>

										{/* Time display */}
										<span className="text-sm font-mono">
											{formatTime(currentTime)} / {formatTime(duration)}
										</span>
									</div>

									{/* Right controls */}
									<div className="flex items-center space-x-2">
										{/* Playback speed */}
										<select
											value={playbackRate}
											onChange={(e) =>
												handlePlaybackRateChange(Number(e.target.value))
											}
											className="bg-transparent border border-white/30 rounded px-2 py-1 text-sm text-white"
											aria-label={t("playbackSpeed")}
										>
											<option value={0.5} className="text-black">
												0.5x
											</option>
											<option value={0.75} className="text-black">
												0.75x
											</option>
											<option value={1} className="text-black">
												1x
											</option>
											<option value={1.25} className="text-black">
												1.25x
											</option>
											<option value={1.5} className="text-black">
												1.5x
											</option>
											<option value={2} className="text-black">
												2x
											</option>
										</select>

										<Button
											variant="ghost"
											size="sm"
											onClick={toggleFullscreen}
											className="text-white hover:text-white hover:bg-white/20"
											aria-label={isFullscreen ? t("exitFullscreen") : t("enterFullscreen")}
										>
											<Maximize className="w-4 h-4" aria-hidden="true" />
										</Button>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Center play button when paused */}
					{!isPlaying && !isLoading && (
						<div className="absolute inset-0 flex items-center justify-center">
							<Button
								variant="ghost"
								size="lg"
								onClick={togglePlayPause}
								className="bg-black/50 text-white hover:bg-black/70 hover:text-white rounded-full p-4"
								aria-label={t("playVideo")}
							>
								<Play className="w-12 h-12" aria-hidden="true" />
							</Button>
						</div>
					)}
				</div>

				{/* Keyboard shortcuts info */}
				<div className="p-4 border-t bg-muted/30">
					<p className="text-xs text-muted-foreground">
						<strong>{t("keyboardShortcuts")}:</strong> {t("shortcutsDescription")}
					</p>
				</div>
			</CardContent>
		</Card>
	);
};

export default VideoPlayer;
